import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { User } from "lucide-react";
import api, { resolveAsset } from "@/lib/api";
import PageHeader from "@/components/site/PageHeader";
import PageSeo from "@/components/site/PageSeo";
import { usePageCopy } from "@/contexts/SettingsContext";

const CATS = ["All", "Launches", "Reviews", "News", "Tips", "EV"];

const NewsPage = () => {
    const [news, setNews] = useState([]);
    const [params, setParams] = useSearchParams();
    const cat = params.get("category") || "All";
    const copy = usePageCopy("news", { title: "News & tips", ctaLabel: "Browse bikes" });

    const setCat = (c) => {
        const next = new URLSearchParams(params);
        if (c === "All") next.delete("category");
        else next.set("category", c);
        setParams(next);
    };

    useEffect(() => {
        const q = cat === "All" ? "" : `?category=${encodeURIComponent(cat)}`;
        api.get(`/news${q}`).then(r => setNews(Array.isArray(r.data) ? r.data : [])).catch(() => setNews([]));
    }, [cat]);

    const featured = news[0];
    const rest = news.slice(1);

    return (
        <section className="container-x py-10" data-testid="news-page">
            <PageSeo pageKey="news" />
            <PageHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} testId="news-header">
                <Link to="/bikes" className="inline-block mt-3 text-sm font-semibold text-[#E31837] hover:underline">{copy.ctaLabel || "Browse bikes"} →</Link>
            </PageHeader>

            <div className="flex flex-wrap gap-2 mb-8">
                {CATS.map(c => (
                    <button key={c} onClick={() => setCat(c)} className={`pill ${cat === c ? "active" : ""}`} data-testid={`news-cat-${c.toLowerCase()}`}>
                        {c}
                    </button>
                ))}
            </div>

            {featured && (
                <Link to={`/news/${featured.slug}`} className="block bike-card overflow-hidden mb-8 group" data-testid="news-featured">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                            <img src={resolveAsset(featured.image)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-6 md:p-8 flex flex-col justify-center">
                            <span className="chip chip-red text-[10px] w-fit">{featured.category}</span>
                            <h2 className="font-display font-bold text-2xl md:text-3xl mt-3 leading-tight group-hover:text-[#E31837] transition-colors">{featured.title}</h2>
                            <p className="text-sm text-[#4a4a52] mt-3 line-clamp-3">{featured.excerpt}</p>
                            <p className="text-xs text-[#7a7a84] mt-4 flex items-center gap-2"><User size={12} /> {featured.author}</p>
                        </div>
                    </div>
                </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map(n => (
                    <Link key={n.id} to={`/news/${n.slug}`} className="bike-card overflow-hidden group" data-testid={`news-card-${n.slug}`}>
                        <div className="aspect-[16/10] overflow-hidden">
                            <img src={resolveAsset(n.image)} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-5">
                            <span className="chip text-[10px]">{n.category}</span>
                            <h3 className="font-display font-bold text-lg mt-2 leading-tight group-hover:text-[#E31837] transition-colors">{n.title}</h3>
                            <p className="text-sm text-[#4a4a52] mt-2 line-clamp-2">{n.excerpt}</p>
                        </div>
                    </Link>
                ))}
            </div>

            {news.length === 0 && (
                <div className="bike-card p-10 text-center text-[#7a7a84]">No articles in this category yet.</div>
            )}
        </section>
    );
};

export default NewsPage;
